// eslint-disable-next-line
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

export interface IPagedDataStore<T> {
	readonly page: number;
	readonly perPage: number;
	readonly total: number;
	readonly isLoading: boolean;
	readonly list: T[];
	readonly sortBy: string;
	readonly sortDir: string;
	sortData: (by: string, dir: string) => void;
	loadPage: (page: number) => void;
}

export interface IPagedData<T> {
	data: T[];
	current_page: number;
	first_page_url?: string;
	from: number;
	last_page: number;
	last_page_url?: string;
	next_page_url: string;
	path: string;
	per_page: number;
	prev_page_url?: string;
	to: number;
	total: number;
}

export interface IFetchListArgs {
	page: number;
	perPage: number;
	sortBy?: string;
	sortDir?: string;
}

abstract class PagedDataStore<T> implements IPagedDataStore<T> {
	constructor() {
		makeObservable(this);
	}

	perPage: number = 50;

	@observable protected _list: T[] = [];
	@computed get list() {
		return this._list;
	}

	@observable protected _page: number = 1;
	@computed get page() {
		return this._page;
	}

	@observable protected _total: number = 0;
	@computed get total() {
		return this._total;
	}

	@observable protected _isLoading: boolean = false;
	@computed get isLoading() {
		return this._isLoading;
	}

	@observable protected _sortBy: string = '';
	@computed get sortBy() {
		return this._sortBy;
	}

	@observable protected _sortDir: string = '';
	@computed get sortDir() {
		return this._sortDir;
	}

	@action.bound
	async loadPage(page: number) {
		this._isLoading = true;
		const result = await this.fetchList({
			page: page,
			perPage: this.perPage,
			sortBy: this._sortBy,
			sortDir: this._sortDir,
		});
		runInAction(() => {
			this._page = page;			
			this._isLoading = false;
			if (result) {
				this._list = result.data;
				this._total = result.total;
			}
		});
	}

	@action.bound
	async sortData(by: string, dir: string) {
		this._isLoading = true;
		this._page = 1;
		const result = await this.fetchList({
			page: this.page,
			perPage: this.perPage,
			sortBy: by,
			sortDir: dir,
		});
		runInAction(() => {
			this._sortBy = by;
			this._sortDir = dir;
			this._isLoading = false;
			if (result) {
				this._list = result.data;
				this._total = result.total;
			}
		});
	}

	protected abstract fetchList(args: IFetchListArgs): Promise<IPagedData<T>|undefined>
}

export default PagedDataStore;
